import { PromptPurposeBadge } from '@/components/prompts/PromptPurposeBadge';
import { PromptRulesList } from '@/components/prompts/PromptRulesList';
import type { PromptWithVersion } from '@/config/objects/prompts';
import { formatDate, formatDateTime } from '@startupquickstart/frontend';
import {
  Badge,
  Breadcrumbs,
  Button,
  ConfirmDialog,
  DetailPageHeader,
  DetailPageHero,
  EmptyTableState,
  Loading,
  PageContainer,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  CheckCircle2,
  FileText,
  History,
  Pencil,
  RefreshCw,
  Send,
  Star,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const tabs = [
  { label: 'Overview', path: 'overview', icon: BookOpen },
  { label: 'Version History', path: 'versions', icon: History },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Shared context passed to child tab routes */
export interface PromptDetailContext {
  prompt: PromptWithVersion | undefined;
  isLoading: boolean;
}

interface PromptVersion {
  id: string;
  versionNumber: number;
  body: string;
  changelog?: string | Record<string, unknown> | null;
  createdAt: string;
  rules?: Array<{
    id?: string;
    body: string;
    sortOrder: number;
  }>;
}

/**
 * Extract rules from a version's changelog field.
 * The API stores rules as `{ rulesSnapshot: [...] }` inside the changelog JSON.
 */
function extractVersionRules(version: PromptVersion): Array<{ body: string; sortOrder: number }> {
  // If rules are already parsed (future API improvement), use them directly
  if (version.rules && version.rules.length > 0) return version.rules;

  // Parse from changelog
  const changelog = version.changelog;
  if (!changelog) return [];

  try {
    const parsed = typeof changelog === 'string' ? JSON.parse(changelog) : changelog;
    if (parsed?.rulesSnapshot && Array.isArray(parsed.rulesSnapshot)) {
      return parsed.rulesSnapshot;
    }
  } catch {
    // Invalid JSON — ignore
  }

  return [];
}

// ---------------------------------------------------------------------------
// PromptDetailPage
// ---------------------------------------------------------------------------

/**
 * Prompt detail page with tabbed layout (Overview | Version History).
 * Accessible from /settings/prompts/:id
 */
export function PromptDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data, isLoading } = useQuery<{ success: boolean; data: PromptWithVersion }>({
    queryKey: ['Prompt', id],
    queryFn: () => apiClient.get(`/prompts/${id}`),
    enabled: !!id,
  });

  const prompt = data?.data;

  // -------------------------------------------------------------------------
  // Delete
  // -------------------------------------------------------------------------
  const deleteMutation = useMutation({
    mutationFn: async (promptId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/prompts/${promptId}`);
    },
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['Prompt'] });
        navigate('/settings/prompts');
      } else {
        throw new Error(response.error ?? 'Failed to delete prompt');
      }
    },
  });

  const handleDelete = () => {
    if (!prompt) return;
    toast.promise(deleteMutation.mutateAsync(prompt.id), {
      loading: 'Deleting prompt...',
      success: 'Prompt deleted successfully',
      error: (error: Error) => error?.message ?? 'Failed to delete prompt',
    });
  };

  // -------------------------------------------------------------------------
  // Set as default
  // -------------------------------------------------------------------------
  const setDefaultMutation = useMutation({
    mutationFn: async (promptId: string) => {
      return apiClient.put<{ success: boolean }>(`/prompts/${promptId}`, { isDefault: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Prompt'] });
      toast.success('Prompt set as default');
    },
    onError: (err: Error) => {
      toast.error(err?.message ?? 'Failed to set as default');
    },
  });

  // -------------------------------------------------------------------------
  // Redirect base path to /overview
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (id) {
      const basePath = `/settings/prompts/${id}`;
      if (location.pathname === basePath || location.pathname === `${basePath}/`) {
        navigate(`${basePath}/overview`, { replace: true });
      }
    }
  }, [id, location.pathname, navigate]);

  const basePath = `/settings/prompts/${id}`;

  const breadcrumbs = useMemo(
    () => [
      { label: 'Home', href: '/' },
      { label: 'Settings', href: '/settings/prompts' },
      { label: 'Prompts', href: '/settings/prompts' },
      { label: prompt?.name ?? 'Prompt', current: true },
    ],
    [prompt?.name],
  );

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-lg font-medium mb-2">Prompt Not Found</h2>
          <p className="text-muted-foreground">The requested prompt could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer padding="none" className="flex flex-col overflow-hidden">
      <DetailPageHeader
        actions={
          prompt && (
            <>
              {!prompt.isDefault && (
                <Button
                  variant="outline"
                  onClick={() => setDefaultMutation.mutate(prompt.id)}
                  loading={setDefaultMutation.isPending}
                >
                  <Star className="h-4 w-4" />
                  Set as Default
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )
        }
      >
        <Breadcrumbs items={breadcrumbs} />
      </DetailPageHeader>

      <DetailPageHero
        isLoading={isLoading}
        category={{ label: 'Prompt', icon: FileText }}
        title={prompt?.name ?? 'Prompt'}
        badges={
          <>
            {prompt?.purpose && <PromptPurposeBadge purpose={prompt.purpose} />}
            {prompt?.isDefault && (
              <Badge
                variant="outline"
                className="bg-green-400/20 text-green-300 border-green-400/30"
              >
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Default
              </Badge>
            )}
            {prompt?.activeVersion && (
              <Badge variant="outline" className="bg-white/10 text-white/80 border-white/20">
                v{prompt.activeVersion.versionNumber}
              </Badge>
            )}
          </>
        }
        tabs={tabs}
        basePath={basePath}
      >
        <Outlet context={{ prompt, isLoading } satisfies PromptDetailContext} />
      </DetailPageHero>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Prompt"
        description={`Are you sure you want to delete "${prompt?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </PageContainer>
  );
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

/**
 * Overview tab — shows prompt body, metadata, rules editor, and publish action.
 */
export function PromptOverviewTab() {
  const { prompt, isLoading } = useOutletContext<PromptDetailContext>();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);

  const publishMutation = useMutation({
    mutationFn: async () => {
      if (!prompt?.activeVersion?.body && !prompt) {
        throw new Error('No prompt body to publish');
      }
      // Snapshot the current active version body (or empty string if none yet)
      const body = prompt?.activeVersion?.body ?? '';
      return apiClient.post(`/prompts/${prompt!.id}/publish`, { body });
    },
    onSuccess: () => {
      setShowPublishConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['Prompt', prompt?.id] });
      queryClient.invalidateQueries({ queryKey: ['PromptVersions', prompt?.id] });
      toast.success('New version published successfully');
    },
    onError: (err: Error) => {
      toast.error(err?.message ?? 'Failed to publish prompt');
    },
  });

  // Detect whether the draft has changed from the latest published version.
  // Must be called before early returns to satisfy React hooks rules.
  const hasDraftChanges = useMemo(() => {
    if (!prompt) return false;
    if (!prompt.activeVersion) return true; // never published — always show

    const publishedRules = extractVersionRules(prompt.activeVersion as PromptVersion);
    const currentRules = prompt.rules ?? [];

    // Rules count changed?
    if (currentRules.length !== publishedRules.length) return true;

    // Rule text or order changed?
    const sortedCurrent = [...currentRules].sort((a, b) => a.sortOrder - b.sortOrder);
    const sortedPublished = [...publishedRules].sort((a, b) => a.sortOrder - b.sortOrder);
    for (let i = 0; i < sortedCurrent.length; i++) {
      if (sortedCurrent[i].body !== sortedPublished[i].body) return true;
    }

    return false;
  }, [prompt]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (!prompt) return null;

  const body = prompt.activeVersion?.body;

  return (
    <div className="space-y-6">
      {/* Publish action banner — only shown when draft differs from published */}
      {hasDraftChanges && (
        <div className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 shadow-sm px-6 py-4">
          <div>
            <h2 className="text-base font-medium tracking-tight text-zinc-900">Unpublished Changes</h2>
            <p className="text-sm text-muted-foreground">
              The current draft differs from the published version. Publish to make changes live.
            </p>
          </div>
          <Button
            onClick={() => setShowPublishConfirm(true)}
            className="shrink-0"
          >
            <Send className="h-4 w-4" />
            Publish
          </Button>
        </div>
      )}

      {/* Body + Details side by side, top-aligned with matching height */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
        {/* Left — prompt body (stretches to match details height) */}
        <div className="lg:col-span-3">
          <div className="rounded-xl border bg-card shadow-sm h-full flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium tracking-tight text-zinc-900">Prompt Body</h2>
                <p className="text-sm text-muted-foreground">
                  Active version content with{' '}
                  <code className="text-xs bg-muted px-1 py-0.5 rounded">{'{{variable}}'}</code>{' '}
                  placeholders
                </p>
              </div>
              {prompt.activeVersion && (
                <Badge variant="secondary">v{prompt.activeVersion.versionNumber}</Badge>
              )}
            </div>
            <div className="p-6 flex-1">
              {body ? (
                <pre className="text-sm text-foreground whitespace-pre-wrap font-mono leading-relaxed bg-muted/30 rounded-lg p-4 overflow-auto h-full">
                  {body}
                </pre>
              ) : (
                <EmptyTableState
                  icon={<FileText className="h-6 w-6" />}
                  title="No active version"
                  description="Edit to add prompt content"
                />
              )}
            </div>
          </div>
        </div>

        {/* Right — metadata details */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">Details</h2>
            </div>
            <div className="p-6">
              <div className="divide-y">
                <div className="py-3 first:pt-0">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                    Purpose
                  </div>
                  <PromptPurposeBadge purpose={prompt.purpose} />
                </div>
                <div className="py-3">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                    Default
                  </div>
                  <div className="text-sm">
                    {prompt.isDefault ? (
                      <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Yes — default for this purpose
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground italic">Not default</span>
                    )}
                  </div>
                </div>
                <div className="py-3">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                    Active Version
                  </div>
                  <div className="text-sm">
                    {prompt.activeVersion ? (
                      <Badge variant="secondary">v{prompt.activeVersion.versionNumber}</Badge>
                    ) : (
                      <span className="text-muted-foreground italic">None</span>
                    )}
                  </div>
                </div>
                <div className="py-3">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                    Last Updated
                  </div>
                  <div className="text-sm">{formatDate(prompt.updatedAt)}</div>
                </div>
                <div className="py-3">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                    Created
                  </div>
                  <div className="text-sm">{formatDate(prompt.createdAt)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rules — full width */}
      <div className="rounded-xl border bg-card shadow-sm">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-medium tracking-tight text-zinc-900">Rules</h2>
          <p className="text-sm text-muted-foreground">
            Ordered rules applied to this prompt. Toggle, reorder, edit, or delete inline.
          </p>
        </div>
        <div className="p-6">
          <PromptRulesList
            promptId={prompt.id}
            rules={prompt.rules ?? []}
          />
        </div>
      </div>

      {/* Publish confirmation */}
      <ConfirmDialog
        open={showPublishConfirm}
        onOpenChange={setShowPublishConfirm}
        title="Publish New Version"
        description="This will create a new version of this prompt, snapshotting the current body and rules. The new version will become the active version."
        confirmText="Publish"
        loading={publishMutation.isPending}
        onConfirm={() => { publishMutation.mutate(); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Version History tab
// ---------------------------------------------------------------------------

/**
 * Version History tab — paginated table of all versions, with activate action
 * and an expandable version detail panel.
 */
export function PromptVersionHistoryTab() {
  const { prompt, isLoading: promptLoading } = useOutletContext<PromptDetailContext>();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [activateTarget, setActivateTarget] = useState<string | null>(null);

  const { data: versionsData, isLoading: versionsLoading } = useQuery<{
    success: boolean;
    items?: PromptVersion[];
    data?: PromptVersion[];
    pagination?: { total: number; page: number; limit: number; hasNext: boolean; hasPrev: boolean };
  }>({
    queryKey: ['PromptVersions', prompt?.id],
    queryFn: () => apiClient.get(`/prompts/${prompt!.id}/versions`),
    enabled: !!prompt?.id,
  });

  const { data: versionDetailData, isLoading: versionDetailLoading } = useQuery<{
    success: boolean;
    data: PromptVersion;
  }>({
    queryKey: ['PromptVersion', prompt?.id, selectedVersionId],
    queryFn: () => apiClient.get(`/prompts/${prompt!.id}/versions/${selectedVersionId}`),
    enabled: !!prompt?.id && !!selectedVersionId,
  });

  const activateMutation = useMutation({
    mutationFn: async (versionId: string) => {
      return apiClient.post(`/prompts/${prompt!.id}/versions/${versionId}/activate`, {});
    },
    onSuccess: () => {
      setActivateTarget(null);
      queryClient.invalidateQueries({ queryKey: ['Prompt', prompt?.id] });
      queryClient.invalidateQueries({ queryKey: ['PromptVersions', prompt?.id] });
      toast.success('Version activated successfully');
    },
    onError: (err: Error) => {
      toast.error(err?.message ?? 'Failed to activate version');
    },
  });

  const isLoading = promptLoading || versionsLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (!prompt) return null;

  const versions = versionsData?.items ?? versionsData?.data ?? [];
  // Newest first
  const sorted = [...versions].sort((a, b) => b.versionNumber - a.versionNumber);
  const selectedVersion = versionDetailData?.data;
  const activeVersionId = prompt.activeVersionId;

  return (
    <div className="space-y-6">
      {/* Versions table */}
      <div className="rounded-xl border bg-card shadow-sm">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium tracking-tight text-zinc-900">Version History</h2>
            <p className="text-sm text-muted-foreground">All published versions, newest first</p>
          </div>
          {versionsLoading && <Loading />}
        </div>

        {sorted.length === 0 ? (
          <div className="p-6">
            <EmptyTableState
              icon={<History className="h-6 w-6" />}
              title="No versions yet"
              description="Publish a version from the Overview tab to see it here"
            />
          </div>
        ) : (
          <div className="divide-y">
            {sorted.map((version) => {
              const isActive = version.id === activeVersionId;
              const isSelected = selectedVersionId === version.id;

              return (
                <div
                  key={version.id}
                  className={`flex items-center justify-between px-6 py-4 ${isActive ? 'bg-green-50/50' : ''}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge variant={isActive ? 'default' : 'secondary'}>
                      v{version.versionNumber}
                    </Badge>
                    <div className="min-w-0">
                      <p className="text-sm font-medium">Version {version.versionNumber}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDateTime(version.createdAt)}
                      </p>
                    </div>
                    {isActive && (
                      <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 shrink-0">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Active
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant={isSelected ? 'secondary' : 'outline'}
                      size="sm"
                      onClick={() =>
                        setSelectedVersionId(isSelected ? null : version.id)
                      }
                    >
                      {isSelected ? 'Close' : 'View'}
                    </Button>
                    {!isActive && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setActivateTarget(version.id)}
                        loading={activateMutation.isPending && activateTarget === version.id}
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Activate
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Version detail panel */}
      {selectedVersionId && (
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">
                {selectedVersion ? `Version ${selectedVersion.versionNumber} Detail` : 'Version Detail'}
              </h2>
              <p className="text-sm text-muted-foreground">Read-only snapshot of this version</p>
            </div>
            {selectedVersion && selectedVersion.id !== activeVersionId && (
              <Button
                onClick={() => setActivateTarget(selectedVersion.id)}
                loading={activateMutation.isPending && activateTarget === selectedVersion.id}
              >
                <RefreshCw className="h-4 w-4" />
                Activate This Version
              </Button>
            )}
          </div>

          {versionDetailLoading ? (
            <div className="p-6 flex justify-center">
              <Loading />
            </div>
          ) : selectedVersion ? (
            (() => {
              const versionRules = extractVersionRules(selectedVersion);
              return (
                <div className="p-6 space-y-6">
                  {/* Body */}
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">
                      Prompt Body
                    </h3>
                    <pre className="text-sm text-foreground whitespace-pre-wrap font-mono leading-relaxed bg-muted/30 rounded-lg p-4 overflow-auto">
                      {selectedVersion.body}
                    </pre>
                  </div>

                  {/* Rules snapshot */}
                  {versionRules.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">
                        Rules Snapshot ({versionRules.length} rules)
                      </h3>
                      <div className="rounded-lg border divide-y">
                        {[...versionRules]
                          .sort((a, b) => a.sortOrder - b.sortOrder)
                          .map((rule, idx) => (
                            <div key={idx} className="px-4 py-3 flex items-start gap-3">
                              <span className="flex-shrink-0 h-5 w-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                                {idx + 1}
                              </span>
                              <p className="text-sm text-foreground leading-relaxed">{rule.body}</p>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()
          ) : null}
        </div>
      )}

      {/* Activate confirmation */}
      <ConfirmDialog
        open={!!activateTarget}
        onOpenChange={(open) => { if (!open) setActivateTarget(null); }}
        title="Activate Version"
        description={(() => {
          const target = sorted.find((v) => v.id === activateTarget);
          return target
            ? `Activate version ${target.versionNumber}? This will replace the current active version.`
            : 'Activate this version?';
        })()}
        confirmText="Activate"
        loading={activateMutation.isPending}
        onConfirm={() => { if (activateTarget) activateMutation.mutate(activateTarget); }}
      />
    </div>
  );
}
